import React, { useEffect, useMemo, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';

const fallbackActivity = require('./../../assets/images/fallback/activity.png');

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/scores', setUserInfo, setLoading, setError, []);
  }, []);

  const scores = userInfo?.scores || [];

  const totals = useMemo(() => {
    let place = 0;
    let joker = 0;
    let ladies = 0;
    scores.forEach((s) => {
      place += s.score || 0;
      joker += s.joker || 0;
      ladies += s.ladies || 0;
    });
    return { total: place + joker + ladies, place, joker, ladies, count: scores.length };
  }, [scores]);

  return (
    <Base activeNav="home">
      <Card mb="2">
        <Back to="/home" />
        <Title>
          <Icon name="hundred-points" className="me-2" style={{ fontSize: '1.4rem' }} theme="primary" />
          {t('scores')}
        </Title>

        {scores.length > 0 && (
          <>
            <div className="scores-summary">
              <div className="scores-summary__stat">
                <div className="scores-summary__stat-value">{totals.total}</div>
                <div className="scores-summary__stat-label">{t('pts')}</div>
              </div>
              <div className="scores-summary__divider" aria-hidden="true" />
              <div className="scores-summary__stat">
                <div className="scores-summary__stat-value">{totals.count}</div>
                <div className="scores-summary__stat-label">{t('activities')}</div>
              </div>
            </div>

            {(totals.place > 0 || totals.joker !== 0 || totals.ladies !== 0) && (
              <div className="score-breakdown score-breakdown--summary">
                <span className="score-breakdown__item score-breakdown__item--place">
                  <span className="score-breakdown__label">{t('place')}</span>
                  <span className="score-breakdown__value">+{totals.place}</span>
                </span>
                {totals.joker !== 0 && (
                  <span className="score-breakdown__item score-breakdown__item--joker">
                    <Icon name="star" theme="primary" />
                    <span className="score-breakdown__label">{t('joker')}</span>
                    <span className="score-breakdown__value">+{totals.joker}</span>
                  </span>
                )}
                {totals.ladies !== 0 && (
                  <span className="score-breakdown__item score-breakdown__item--ladies">
                    <Icon name="woman-head" />
                    <span className="score-breakdown__label">{t('ladies')}</span>
                    <span className="score-breakdown__value">+{totals.ladies}</span>
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {scores.length > 0 ? (
        scores.map((score) => {
          const place = score.score || 0;
          const joker = score.joker || 0;
          const ladies = score.ladies || 0;
          const total = place + joker + ladies;
          const hasJoker = joker !== 0;
          const hasLadies = ladies !== 0;
          return (
            <Card
              key={score.id}
              to="/score"
              onClick={() => sessionStorage.setItem('selectedScoreId', score.id)}
              mb="2"
              className="score-card"
            >
              <div className="score-card__row">
                <Image
                  size="3.5rem"
                  className="score-card__image"
                  url={score.photo_url ? score.photo_url : fallbackActivity}
                />

                <div className="score-card__body">
                  <div className="score-card__name">{score.name}</div>
                  <div className="score-breakdown">
                    <span className="score-breakdown__item score-breakdown__item--place">
                      <span className="score-breakdown__label">{t('place')}</span>
                      <span className="score-breakdown__value">+{place}</span>
                    </span>
                    {hasJoker && (
                      <span className="score-breakdown__item score-breakdown__item--joker">
                        <Icon name="star" theme="primary" />
                        <span className="score-breakdown__label">{t('joker')}</span>
                        <span className="score-breakdown__value">+{joker}</span>
                      </span>
                    )}
                    {hasLadies && (
                      <span className="score-breakdown__item score-breakdown__item--ladies">
                        <Icon name="woman-head" />
                        <span className="score-breakdown__label">{t('ladies')}</span>
                        <span className="score-breakdown__value">+{ladies}</span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="score-card__points">
                  <span className="score-card__points-value">+{total}</span>
                  <span className="score-card__points-label">{t('pts')}</span>
                </div>
              </div>
            </Card>
          );
        })
      ) : (
        <NotFound translationKey="noScoresFound" loading={loading} />
      )}
    </Base>
  );
};

export default Page;
